import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, FormGroup } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";

import { ILineITemExtended } from "@components/purchase-orders/models/purchase-order.interface";
import { PurchaseOrdersService } from "@components/purchase-orders/purchase-orders.service";
import { BackendErrorConstants, TextConstants } from "@shared/constants";
import { RecordStateEnum } from "@shared/enums";
import {
  IBaseUnit,
  ICustomUnitOfMeasurement,
  IProductExtended,
  ISelectOption,
} from "@shared/interfaces";
import { AuthenticationService, NotificationService, ProductsService } from "@shared/services";
import { CommonUtils, FormUtils } from "@shared/utils";
import { CustomValidators } from "@shared/validators";

@Component({
  selector: "app-add-purchase-order-item-dialog",
  templateUrl: "./add-purchase-order-item-dialog.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AddPurchaseOrderItemDialogComponent implements OnInit {
  private readonly productsService = inject(ProductsService);

  private readonly authenticationService = inject(AuthenticationService);

  private readonly notificationService = inject(NotificationService);

  private readonly dialogRef = inject(MatDialogRef<AddPurchaseOrderItemDialogComponent>);

  private readonly purchaseService = inject(PurchaseOrdersService);

  private readonly data: {
    lineItem?: ILineITemExtended;
    allLineItems?: ILineITemExtended[];
  } = inject(MAT_DIALOG_DATA);

  private allProducts = signal<IProductExtended[]>([]);

  private selectedProduct = signal<IProductExtended>(null);

  private readonly destroyRef = inject(DestroyRef);

  private initialQuantity: number = 0;

  formGroup = new FormGroup({
    lineItemId: new FormControl(null, [
      CustomValidators.required,
      CustomValidators.isNotInList(
        (this.data?.allLineItems || []).map((item) => item.lineItemId),
        this.data?.lineItem?.lineItemId,
        !!this.data?.lineItem,
        BackendErrorConstants.DUPLICATE_ITEM_ID,
      ),
    ]),
    product: new FormControl(null, [
      CustomValidators.required,
      CustomValidators.isNotInList(
        (this.data?.allLineItems || []).map((item) => item.product?.id),
        this.data?.lineItem?.product?.id,
        !!this.data?.lineItem,
        BackendErrorConstants.DUPLICATE_PRODUCT,
      ),
    ]),
    quantity: new FormControl(null, [CustomValidators.required, CustomValidators.min(1)]),
    unit: new FormControl({ value: null, disabled: !this.data?.lineItem }, [
      CustomValidators.required,
    ]),
  });

  isLoading = signal<boolean>(false);

  isEditing = signal<boolean>(false);

  allProductsOptions = signal<ISelectOption[]>([]);

  unitOfMeasurementOptions = signal<ISelectOption[]>([]);

  baseUnitOfMeasurement = signal<IBaseUnit>(null);

  itemDefaultUnit = signal<IBaseUnit | ICustomUnitOfMeasurement>(null);

  readonly translations = {
    product: TextConstants.PRODUCT,
    item: TextConstants.ITEM_ID_NUMBER,
    unit: $localize`:@@orderitemdialog.unit:Unit`,
    quantity: TextConstants.QUANTITY,
    suffixIconTp: $localize`:@@purchaseorder.suffixIconTp:Generate line item number`,
  };

  async ngOnInit() {
    if (this.data?.lineItem) {
      this.isEditing.set(true);
    }
    const allProducts = await this.productsService.getAllGraphQL(null, null, [
      "CUSTOM_UNITS",
      "DEFAULT_CUSTOM_UNIT",
    ]);

    this.allProducts.set(allProducts);
    const shouldFilter = !this.isEditing();
    const filteredProducts = shouldFilter
      ? allProducts.filter(({ recordState }) => recordState === RecordStateEnum.ACTIVE)
      : allProducts;
    const allProductsOptions = filteredProducts.map((p) => ({ label: p.name, value: p.id }));

    this.allProductsOptions.set(allProductsOptions);

    this.setUpForm();
  }

  setUpForm() {
    const lineItem = this.data?.lineItem as ILineITemExtended;

    if (lineItem) {
      const product = this.allProductsOptions().find((p) => p.value === lineItem?.product?.id);

      this.formGroup.controls.lineItemId.setValue(lineItem.lineItemId);
      setTimeout(() => this.formGroup.controls.product.setValue(product));

      const quantity = this.getQuantity(lineItem.quantity, lineItem.product);

      this.initialQuantity = lineItem.quantity;
      this.formGroup.controls.quantity.setValue(quantity);
    }

    this.formGroup.controls.product.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => this.onProductChanged(v));

    this.formGroup.controls.unit.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => this.setItemDefaultUnitOfMeasurement(v));

    this.formGroup.controls.quantity.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string) => {
        const parsed = +value;

        const itemDefaultUnit = this.itemDefaultUnit();
        const baseUnitOfMeasurement = this.baseUnitOfMeasurement();

        if (
          itemDefaultUnit?.id !== baseUnitOfMeasurement?.id &&
          itemDefaultUnit &&
          baseUnitOfMeasurement
        ) {
          this.initialQuantity = CommonUtils.convertFromCustomToSystem(
            parsed,
            itemDefaultUnit,
            baseUnitOfMeasurement,
          );
        } else {
          this.initialQuantity = parsed;
        }
      });
  }

  async onSubmit() {
    if (this.formGroup.invalid) {
      FormUtils.findAndMarkInvalidControls(this.formGroup);
      this.notificationService.showError(TextConstants.FILL_REQUIRED_FIELDS);

      return;
    }
    try {
      this.isLoading.set(true);
      const formValue = this.formGroup.value;

      let quantity: number = formValue.quantity;

      const itemDefaultUnit = this.itemDefaultUnit();
      const baseUnitOfMeasurement = this.baseUnitOfMeasurement();

      if (
        itemDefaultUnit?.id !== baseUnitOfMeasurement?.id &&
        itemDefaultUnit &&
        baseUnitOfMeasurement
      ) {
        quantity = CommonUtils.convertFromCustomToSystem(
          quantity,
          itemDefaultUnit,
          baseUnitOfMeasurement,
        );
      }
      const item = {
        lineItemId: formValue.lineItemId,
        quantity,
        product: `/organisations/${this.authenticationService.getActiveOrganisationId()}/products/${formValue.product.value}`,
      };

      await this.purchaseService.upsertItem(item, this.data?.lineItem?.lineItemId);

      const message = this.isEditing()
        ? $localize`:@@orderitemdialog.itemEdited:Line item modified`
        : $localize`:@@orderitemdialog.itemAdded:Line item added`;

      this.notificationService.showSuccess(message);
      this.onClose(true);
    } catch (error) {
      this.notificationService.showError(error);
    } finally {
      this.isLoading.set(false);
    }
  }

  onClose(hasSaved: boolean) {
    this.dialogRef.close({ hasSaved });
  }

  onProductChanged(selectedProduct: ISelectOption) {
    if (!selectedProduct) {
      this.formGroup.controls.unit.setValue(null);
      FormUtils.disableControls(this.formGroup, ["unit"]);
      this.selectedProduct.set(null);
      this.unitOfMeasurementOptions.set([]);

      return;
    }

    const product = this.allProducts().find((p) => p.id === selectedProduct.value);

    FormUtils.enableControls(this.formGroup, ["unit"]);
    this.selectedProduct.set(product);
    this.setUnitOfMeasurementOptions(product);
  }

  private async setUnitOfMeasurementOptions(product: IProductExtended): Promise<void> {
    const customUnitsOfMeasurement = product.customUnits || [];

    this.baseUnitOfMeasurement.set(product.unitOfMeasurement);
    const unitOfMeasurementOptions = [product.unitOfMeasurement, ...customUnitsOfMeasurement].map(
      (unit) => ({
        label: unit.name,
        value: unit.id,
      }),
    );

    this.unitOfMeasurementOptions.set(unitOfMeasurementOptions);
    this.itemDefaultUnit.set(product.defaultCustomUnit ?? product.unitOfMeasurement);

    const defaultUnit = this.unitOfMeasurementOptions().find(
      (unit) => unit.value === this.itemDefaultUnit().id,
    );

    setTimeout(() => {
      if (defaultUnit) {
        this.formGroup.controls.unit.setValue(defaultUnit);
      }
      this.formGroup.updateValueAndValidity();
    });
  }

  private async setItemDefaultUnitOfMeasurement(selectedUnit: ISelectOption): Promise<void> {
    if (!selectedUnit || !this.baseUnitOfMeasurement()) {
      return;
    }

    if (selectedUnit.value === this.baseUnitOfMeasurement().id) {
      this.itemDefaultUnit.set(this.baseUnitOfMeasurement());
    } else {
      if (!this.selectedProduct()) {
        return;
      }
      this.itemDefaultUnit.set(this.selectedProduct().defaultCustomUnit);
    }

    const quantityControl = this.formGroup.controls.quantity;
    let value: number;

    const itemDefaultUnit = this.itemDefaultUnit();
    const baseUnitOfMeasurement = this.baseUnitOfMeasurement();

    if (!itemDefaultUnit || !baseUnitOfMeasurement) {
      return;
    }

    if (itemDefaultUnit.id !== baseUnitOfMeasurement.id) {
      value = CommonUtils.convertFromSystemToCustom(
        this.initialQuantity,
        itemDefaultUnit,
        baseUnitOfMeasurement,
      );
      if (value) {
        quantityControl.setValue(value, { emitEvent: false });
      }
    } else {
      quantityControl.setValue(this.initialQuantity, { emitEvent: false });
    }
  }

  private getQuantity(quantity: number, product: IProductExtended): number {
    if (product.defaultCustomUnit) {
      return CommonUtils.convertFromSystemToCustom(
        quantity,
        product.defaultCustomUnit,
        product.unitOfMeasurement,
      );
    } else {
      return quantity;
    }
  }

  public get isSubmitButtonDisabled(): boolean {
    if (!this.isEditing()) {
      return false;
    }

    return !this.formGroup || this.formGroup.invalid;
  }

  generateItemNumber() {
    const number = CommonUtils.generateRandomString(10);

    this.formGroup.controls["lineItemId"].setValue(number);
  }
}
